import React, { useState, useEffect } from "react";
import api from "../../utils/api";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Grid,
  Typography,
  Alert,
  Card,
  CardContent,
  Tooltip,
  LinearProgress,
  Button,
} from "@mui/material";
import {
  People,
  AccountBalance,
  Receipt,
  TrendingUp,
  Warning,
  CheckCircle,
  Schedule,
  ArrowBack,
  Logout as LogoutIcon,
} from "@mui/icons-material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ChartTooltip,
  ResponsiveContainer,
} from "recharts";
import Sidebar from "./Sidebar";
import "./Dashboard.css";
import {
  successAlert,
  errorAlert,
  loadingAlert,
  closeAlert,
} from "../../utils/swalConfig";

// Add this formatter function at the top with other functions
const formatDate = (date) => {
  return new Date(date).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalAvailableBalance: 0,
    totalBonusWallet: 0,
    totalVolume: 0,
    totalTransactions: 0,
    totalBanks: 0,
    activeUsers: 0,
    pendingTransactions: 0,
    successfulTransactions: 0,
    failedTransactions: 0,
    recentTransactions: [], // Initialize as empty array
    userGrowth: [], // Initialize as empty array
    transactionTrends: [], // Initialize as empty array
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        loadingAlert("Loading dashboard...");
        const response = await api.get("admin/stats");

        if (response.data.status === "success") {
          setStats(response.data.data);
          setError(null);
        } else {
          setError("Failed to fetch stats");
          errorAlert("Error!", "Failed to fetch dashboard stats");
        }
      } catch (err) {
        // console.error("Dashboard error:", err);

        // Network or server error
        if (!err.response) {
          setError("Network error - please check your connection");
          errorAlert("Error!", "Network error - please check your connection");
          return;
        }

        // Authentication error
        if (err.response.status === 401) {
          localStorage.removeItem("jwt");
          localStorage.removeItem("userRole");
          navigate("/login");
          return;
        }

        // Other errors
        const errorMessage =
          err.response?.data?.message || "Failed to load dashboard stats";
        setError(errorMessage);
        errorAlert("Error!", errorMessage);
      } finally {
        closeAlert();
        setLoading(false);
      }
    };

    fetchStats();

    const interval = setInterval(fetchStats, 300000); // 5 minutes
    return () => clearInterval(interval);
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem("jwt");
    localStorage.removeItem("userRole");
    navigate("/login");
  };

  const StatCard = ({ title, value, icon, color, percentage }) => (
    <Paper
      sx={{
        p: 2,
        display: "flex",
        flexDirection: "column",
        height: 140,
        bgcolor: color,
        color: "white",
        transition: "transform 0.3s",
        "&:hover": {
          transform: "translateY(-5px)",
          boxShadow: 3,
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        {icon}
        <Typography variant="h6" sx={{ ml: 1 }}>
          {title}
        </Typography>
      </Box>
      <Typography variant="h4">{value}</Typography>
      {percentage && (
        <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
          <TrendingUp sx={{ mr: 1, fontSize: 16 }} />
          <Typography variant="body2">{percentage}% increase</Typography>
        </Box>
      )}
    </Paper>
  );

  // Remove the loading CircularProgress and return null instead
  if (loading) {
    return null; // Return null since we're showing loadingAlert
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  // Update the transaction success rate calculation
  const calculateTransactionSuccess = () => {
    const totalTransactions =
      stats.successfulTransactions +
      stats.pendingTransactions +
      stats.failedTransactions;
    if (totalTransactions === 0) return 0;
    return (stats.successfulTransactions / totalTransactions) * 100;
  };

  // Replace the existing calculation
  const transactionSuccess = calculateTransactionSuccess();

  return (
    <Box sx={{ display: "flex" }}>
      <Sidebar />
      <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
        {/* Add navigation controls at the top */}
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
          {/* <Typography variant="h4"></Typography> */}
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBack />}
              onClick={() => navigate("/dashboard")}
            >
              User Dashboard
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<LogoutIcon />}
              onClick={handleLogout}
            >
              Logout
            </Button>
          </Box>
        </Box>

        {/* Main Stats */}
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Total Users"
              value={stats.totalUsers}
              icon={<People />}
              color="#1976d2"
              percentage={5.7}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Total User Balance"
              value={`₦${stats.totalAvailableBalance.toLocaleString()}`}
              icon={<AccountBalance />}
              color="#2e7d32"
              percentage={3.2}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Total Bonus Wallet"
              value={`₦${stats.totalBonusWallet.toLocaleString()}`}
              icon={<AccountBalance sx={{ color: "inherit" }} />}
              color="#00796b"
              percentage={2.8}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Transaction Volume"
              value={`₦${stats.totalVolume.toLocaleString()}`}
              icon={<TrendingUp />}
              color="#9c27b0"
              percentage={8.4}
            />
          </Grid>
        </Grid>

        {/* Transaction Status */}
        <Grid container spacing={3} sx={{ mt: 2 }}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Transaction Success Rate
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Box sx={{ flexGrow: 1, mr: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={transactionSuccess}
                    sx={{ height: 10, borderRadius: 5 }}
                  />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {transactionSuccess.toFixed(1)}%
                </Typography>
              </Box>
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}
              >
                <Tooltip title="Pending Transactions">
                  <Box sx={{ textAlign: "center" }}>
                    <Schedule color="warning" />
                    <Typography variant="body2">
                      {stats.pendingTransactions}
                    </Typography>
                  </Box>
                </Tooltip>
                <Tooltip title="Successful Transactions">
                  <Box sx={{ textAlign: "center" }}>
                    <CheckCircle color="success" />
                    <Typography variant="body2">
                      {stats.successfulTransactions}
                    </Typography>
                  </Box>
                </Tooltip>
                <Tooltip title="Failed Transactions">
                  <Box sx={{ textAlign: "center" }}>
                    <Warning color="error" />
                    <Typography variant="body2">
                      {stats.failedTransactions}
                    </Typography>
                  </Box>
                </Tooltip>
              </Box>
            </Paper>
          </Grid>

          {/* User Growth Chart */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, height: "100%" }}>
              <Typography variant="h6" gutterBottom>
                User Growth Trend
              </Typography>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart
                  data={stats.userGrowth || []}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={formatDate}
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis
                    tickFormatter={(value) => value.toLocaleString()}
                    tick={{ fontSize: 12 }}
                  />
                  <ChartTooltip
                    formatter={(value, name) => [
                      value.toLocaleString(),
                      "Users",
                    ]}
                    labelFormatter={(label) => formatDate(label)}
                  />
                  <Line
                    type="monotone"
                    dataKey="users"
                    stroke="#1976d2"
                    strokeWidth={2}
                    dot={{ r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
              {(!stats.userGrowth || stats.userGrowth.length === 0) && (
                <Box sx={{ textAlign: "center", mt: 2 }}>
                  <Typography color="text.secondary">
                    No growth data available
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>

        {/* Recent Transactions */}
        <Paper sx={{ p: 2, mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Recent Transactions
          </Typography>
          <Grid container spacing={2}>
            {stats.recentTransactions?.length > 0 ? (
              stats.recentTransactions.map((transaction) => (
                <Grid item xs={12} sm={6} md={4} key={transaction._id}>
                  <Card>
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom>
                        {new Date(transaction.date).toLocaleDateString()}
                      </Typography>
                      <Typography variant="h6">
                        ₦{transaction.amount.toLocaleString()}
                      </Typography>
                      <Typography color="textSecondary">
                        {transaction.type}
                      </Typography>
                      <Box
                        sx={{ display: "flex", alignItems: "center", mt: 1 }}
                      >
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: "50%",
                            bgcolor:
                              transaction.status === "successful"
                                ? "success.main"
                                : transaction.status === "pending"
                                ? "warning.main"
                                : "error.main",
                            mr: 1,
                          }}
                        />
                        <Typography variant="body2">
                          {transaction.status}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))
            ) : (
              <Grid item xs={12}>
                <Typography color="textSecondary" align="center">
                  No recent transactions
                </Typography>
              </Grid>
            )}
          </Grid>
        </Paper>
      </Box>
    </Box>
  );
};

export default Dashboard;
